import { gql } from '@apollo/client';
import {
  AB_STAT_BRANCH_RESULT_FRAGMENT,
  FLOW_FRAGMENT,
  FLOW_FRAGMENT_SMALL,
  KEYED_LABEL_FRAGMENT,
  PUB_FLOW_COMPOSITE_STEPS_FRAGMENT,
  PUB_FLOW_STEPS_FRAGMENT,
  GET_FLOW_INSTANCE_FRAGMENT,
  PUB_FLOW_CONFIG_FRAGMENT,
  CONFIGURATION_ERRORS,
} from '../../fragments/publicFragments';

export const GET_SDK_INSTALLED_INFO = gql`
  {
    we {
      id
      metadata
    }
  }
`;

export const GET_AVAILABLE_FLOW_LISTS = gql`
  {
    getAvailableFlowTemplates {
      name
      description
    }
    getFlows {
      ...f_FLow
    }
  }
  ${FLOW_FRAGMENT}
`;

export const GET_FLOWS = gql`
  query GetFlows {
    getFlows {
      ...f_Flow_Small
    }
  }
  ${FLOW_FRAGMENT_SMALL}
`;

export const GQL_Q_GET_ARCHIVED_AUTOMATION = gql`
  query GetArchiveAutomation {
    getFlows(status: [DISABLED, ARCHIVED]) {
      ...f_Flow_Small
    }
  }
  ${FLOW_FRAGMENT_SMALL}
`;

export const GET_FLOW = gql`
  query GetFlow($id: ID!) {
    getFlow(id: $id) {
      ...f_FLow
      instruct
    }
  }
  ${FLOW_FRAGMENT}
`;

export const GET_FLOW_NAME = gql`
  query GetFlow($id: ID!) {
    getFlow(id: $id) {
      id
      name
      __typename
    }
  }
`;

export const GET_FLOW_CONFIGURATION = gql`
  query getFlowConfiguration($id: ID!) {
    getFlowConfiguration(id: $id) {
      ...pub_FlowConfiguration
    }
  }
  ${PUB_FLOW_CONFIG_FRAGMENT}
`;

export const GET_CONFIGURATION_ISSUES = gql`
  query getConfigurationIssues($flowId: ID!, $stepIds: [String]) {
    getConfigurationIssues(flowId: $flowId, stepIds: $stepIds) {
      ...f_Errors
    }
  }
  ${CONFIGURATION_ERRORS}
`;

export const GET_FLOW_STEP_LIBRARY = gql`
  query getFlowStepLibrary {
    getFlowStepLibrary {
      ungrouped {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      triggers {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      extraTriggers {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      conditions {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      extraConditions {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      actions {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      extraActions {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      data {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      engage {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
      services {
        ...pub_FlowStep
        ...pub_CompositeFlowStep
      }
    }
  }
  ${PUB_FLOW_STEPS_FRAGMENT}
  ${PUB_FLOW_COMPOSITE_STEPS_FRAGMENT}
`;

export const GQL_Q_FLOW_INSTANCE = gql`
  query getFlowInstances($inStatus: [FlowInstanceStatus!]) {
    getFlowInstances(inStatus: $inStatus) {
      id
      status
      flow {
        id
        name
      }
      currentStep {
        id
        name
      }
      created
    }
  }
`;

export const GET_FLOW_INSTANCES_WITH_PAGINATION = gql`
  query getFlowInstancesWithPagination(
    $flowId: String!
    $inStatus: [FlowInstanceStatus!]
    $startDate: DateTime
    $endDate: DateTime
    $cursor: PaginationInput
  ) {
    getFlowInstancesWithPagination(
      flowId: $flowId
      inStatus: $inStatus
      startDate: $startDate
      endDate: $endDate
      cursor: $cursor
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        totalSize
      }
      instances {
        id
        flow {
          id
          name
        }
        status
        created
        retryCount
      }
    }
  }
`;

export const GET_FLOW_INSTANCE = gql`
  query getFlowInstance($id: ID!) {
    getFlowInstance(id: $id) {
      ...f_FlowInstance
    }
  }
  ${GET_FLOW_INSTANCE_FRAGMENT}
`;

export const RETRY_INSTANCE = gql`
  mutation retryInstance($originalInstanceId: ID!, $stepId: ID!) {
    retryInstance(originalInstanceId: $originalInstanceId, stepId: $stepId) {
      ...f_FlowInstance
    }
  }
  ${GET_FLOW_INSTANCE_FRAGMENT}
`;

export const GET_RETRIES_FOR_INSTANCE = gql`
  query getRetriesForInstance($instanceId: ID!) {
    getRetriesForInstance(instanceId: $instanceId) {
      ...f_FlowInstance
    }
  }
  ${GET_FLOW_INSTANCE_FRAGMENT}
`;

export const GQL_Q_GET_ACTION_FOR = gql`
  query getActionsFor($id: String, $dataType: DataType) {
    getActionsFor(id: $id, dataType: $dataType) {
      actions {
        id
        name
        forDataType
        inputs {
          id
          name
          type
          required
          placeholder
          tooltip
          readonly
        }
        returns {
          key
          type
        }
      }
    }
  }
`;

export const GQL_Q_LIST_CONNECTIONS = gql`
  query listConnections(
    $ids: [String]
    $types: [ConnectionType]
    $system: Boolean
    $events: [EventType]
    $status: [ConnectionStatus]
    $dataTypes: [DataType]
  ) {
    listConnections(
      ids: $ids
      types: $types
      system: $system
      events: $events
      status: $status
      dataTypes: $dataTypes
    ) {
      id
      name
      system
      status
      canConnect
      canDisconnect
      type
      supportedEvents
      company {
        logo
        domain
        name
        homepageUrl
        supportUrl
        pricingUrl
        categories
      }
    }
  }
`;

export const GQL_Q_GET_SEARCHABLE_TYPES = gql`
  query GetSearchableTypes {
    getSearchableTypes
  }
`;

export const GQL_Q_GET_SUPPORTED_CONNECTION_TYPES = gql`
  query getSupportedConnectionTypes($dataTypes: [DataType!]) {
    getSupportedConnectionTypes(DataTypes: $dataTypes)
  }
`;

export const GQL_Q_LOOKUP_ACTION_INPUT_SELECTION = gql`
  query lookupActionInputSelection($actionId: String!, $inputId: String!, $connectionId: String) {
    lookupActionInputSelection(actionId: $actionId, inputId: $inputId, connectionId: $connectionId) {
      key
      label
    }
  }
`;

export const GQL_Q_LIST_DATA_TYPE_DESCRIPTORS = gql`
  query listDataTypeDescriptors {
    listDataTypeDescriptors {
      type
      label
      isComplex
      isEnum
      operators
    }
  }
`;

export const GQL_Q_GET_TEST_EXAMPLES = gql`
  query getTestExamples($instanceId: String!, $stepId: String) {
    getTestExamples(instanceId: $instanceId, stepId: $stepId) {
      description
      asInput
      described {
        key
        type
        label
        isEditable
        value
        __typename
      }
    }
  }
`;

export const GET_POSSIBLE_CONDITION_PROPERTIES_FOR_STEP = gql`
  query getPossibleConditionPropertiesForStep($flowId: String!, $stepId: String!, $propertyToEval: String) {
    getPossibleConditionPropertiesForStep(flowId: $flowId, stepId: $stepId, propertyToEval: $propertyToEval) {
      ...f_KeyedLabel
    }
  }
  ${KEYED_LABEL_FRAGMENT}
`;

export const GET_POSSIBLE_CONDITION_VALUES_FOR_STEP = gql`
  query getPossibleConditionValuesForStep(
    $flowId: String!
    $stepId: String!
    $property: String
    $propertyType: DataType
  ) {
    getPossibleConditionValuesForStep(
      flowId: $flowId
      stepId: $stepId
      property: $property
      propertyType: $propertyType
    ) {
      ...f_KeyedLabel
    }
  }
  ${KEYED_LABEL_FRAGMENT}
`;

export const GET_VALID_OPERATORS_FOR = gql`
  query getValidOperatorsFor($type: DataType!, $property: String) {
    getValidOperatorsFor(type: $type, property: $property)
  }
`;

export const GET_VERSION_HISTORY_FOR_FLOW = gql`
  query getVersionHistoryForFlow($id: ID!) {
    getVersionHistoryForFlow(id: $id) {
      id
      systemUpdated
      systemCreated
    }
  }
`;

export const GET_POSSIBLE_ENUM_VALUES_FOR_DATA_TYPE = gql`
  query getPossibleEnumValuesForDataType($type: DataType!) {
    getPossibleEnumValuesForDataType(type: $type) {
      ...f_KeyedLabel
    }
  }
  ${KEYED_LABEL_FRAGMENT}
`;

export const GET_NODE_HELPER = gql`
  query getNodeHelper($nodeId: String!) {
    getNodeHelper(nodeId: $nodeId) {
      nodeHelperId
      title
      content
      docsUrls {
        url
        label
      }
    }
  }
`;

export const GET_AB_TEST_STATS = gql`
  query getABTestStats($flowId: String!, $stepId: String!) {
    getABTestStats(flowId: $flowId, stepId: $stepId) {
      key
      totalValue
      subLabel
      helpText
      aResult {
        ...f_ABStatBranchResult
      }
      bResult {
        ...f_ABStatBranchResult
      }
    }
  }
  ${AB_STAT_BRANCH_RESULT_FRAGMENT}
`;
